import React, { useState, useEffect, useRef, useContext } from "react";

import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";

import LogoutPage from './Logout';
import LanguageSwitcher from './LanguageSwitcher';
import defaultAvatar from "../../../images/profile/1.jpg";


import { ThemeContext } from "../../../context/ThemeContext";
import MenuToggleButton from "./MenuToggleButton";
import {
	getMyNotifications,
	getUnreadCount,
	markNotificationRead,
	markAllNotificationsRead,
} from "../../../services/NotificationService";
import { uploadMyAvatar } from "../../../services/UserService";
import { updateUserProfileAction } from "../../../store/actions/AuthActions";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../utils/i18nLabels";
import { MenuList } from "./Menu";
import { getApiErrorMessage } from "../../../utils/apiError";

// Retrouve l'entrée du menu correspondant à l'URL courante (pour le titre de page traduit)
const flattenMenu = (items, acc = []) => {
	items.forEach((it) => {
		if (it.to && it.titleKey) acc.push(it);
		if (Array.isArray(it.content)) flattenMenu(it.content, acc);
	});
	return acc;
};
const MENU_ITEMS = flattenMenu(MenuList);
const normPath = (p) => "/" + String(p || "").replace(/^\/+|\/+$/g, "");
const findMenuTitleKey = (pathname) => {
	const clean = normPath(pathname);
	const candidates = MENU_ITEMS.filter((i) => i.to && i.to !== "#");
	const exact = candidates.find((i) => normPath(i.to) === clean);
	if (exact) return exact.titleKey;
	const parent = candidates
		.filter((i) => clean.startsWith(normPath(i.to) + "/"))
		.sort((a, b) => normPath(b.to).length - normPath(a.to).length)[0];
	return parent ? parent.titleKey : null;
};

const Header = ({ onNote }) => {
	const { t } = useTranslation();
	//For header fixed
	const [headerFix, setheaderFix] = useState(false);
	useEffect(() => {
		window.addEventListener("scroll", () => {
			setheaderFix(window.scrollY > 50);
		});
	}, []);


	const { background, changeBackground } = useContext(ThemeContext);
	const handleThemeMode = () => {
		if (background.value === 'dark') {
			changeBackground({ value: "light", label: "Light" });
		} else {
			changeBackground({ value: "dark", label: "Dark" });
		}
	}

	const [unreadCount, setUnreadCount] = useState(0);
	const [recentNotifications, setRecentNotifications] = useState([]);

	const refreshUnreadCount = () => {
		getUnreadCount()
			.then((res) => setUnreadCount(res.data?.data?.count || 0))
			.catch(() => { });
	};

	useEffect(() => {
		refreshUnreadCount();
		const interval = setInterval(refreshUnreadCount, 30000);
		return () => clearInterval(interval);
	}, []);

	const loadRecentNotifications = () => {
		getMyNotifications({ page: 1, limit: 5 })
			.then((res) => setRecentNotifications(res.data?.data || []))
			.catch(() => { });
	};

	const handleMarkRead = (id) => {
		markNotificationRead(id)
			.then(() => {
				setRecentNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
				refreshUnreadCount();
			})
			.catch(() => { });
	};

	const handleMarkAllRead = () => {
		markAllNotificationsRead()
			.then(() => {
				setRecentNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
				setUnreadCount(0);
			})
			.catch(() => { });
	};

	// Utilisateur connecté (admin, enseignant, étudiant...), pour afficher
	// sa vraie photo/nom/rôle dans la barre de navigation.
	const dispatch = useDispatch();
	const currentUser = useSelector((state) => state.auth?.auth?.user);
	const displayName =
		[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ") ||
		currentUser?.email ||
		t("header.defaultUser");
	const primaryRole = currentUser?.roles?.[0];
	const avatarSrc = currentUser?.avatar_url || defaultAvatar;

	const [avatarUploading, setAvatarUploading] = useState(false);
	const [avatarError, setAvatarError] = useState("");
	const avatarInputRef = useRef(null);

	const handleAvatarClick = (e) => {
		e.stopPropagation();
		avatarInputRef.current?.click();
	};

	const handleAvatarChange = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = ""; // permet de re-choisir le même fichier ensuite
		if (!file) return;

		setAvatarError("");
		setAvatarUploading(true);
		try {
			const res = await uploadMyAvatar(file);
			const updated = res.data?.data;
			dispatch(
				updateUserProfileAction({
					avatar: updated?.avatar,
					avatar_url: updated?.avatar_url,
				})
			);
		} catch (err) {
			setAvatarError(
				getApiErrorMessage(err, t("header.avatar.error"))
			);
		} finally {
			setAvatarUploading(false);
		}
	};

	const [profileOpen, setProfileOpen] = useState(false);
	const profileRef = useRef(null);
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (profileRef.current && !profileRef.current.contains(e.target)) {
				setProfileOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);
	const menuTitleKey = findMenuTitleKey(window.location.pathname);
	const isTakeExamPage = normPath(window.location.pathname).startsWith("/take-exam");
	var path = window.location.pathname.split("/");
	var name = path[path.length - 1].split("-");
	var filterName = name.length >= 3 ? name.filter((n, i) => i > 0) : name;
	var finalName = filterName.includes("app")
		? filterName.filter((f) => f !== "app")
		: filterName.includes("ui")
			? filterName.filter((f) => f !== "ui")
			: filterName.includes("uc")
				? filterName.filter((f) => f !== "uc")
				: filterName.includes("basic")
					? filterName.filter((f) => f !== "basic")
					: filterName.includes("jquery")
						? filterName.filter((f) => f !== "jquery")
						: filterName.includes("table")
							? filterName.filter((f) => f !== "table")
							: filterName.includes("page")
								? filterName.filter((f) => f !== "page")
								: filterName.includes("email")
									? filterName.filter((f) => f !== "email")
									: filterName.includes("ecom")
										? filterName.filter((f) => f !== "ecom")
										: filterName.includes("chart")
											? filterName.filter((f) => f !== "chart")
											: filterName.includes("editor")
												? filterName.filter((f) => f !== "editor")
												: filterName;
	return (
		<div className={`header tw-bg-white dark:tw-bg-[#272738] ${headerFix ? "sticky" : ""}`}>
			<div className="header-content pt-3 pb-3">
				<nav className="navbar navbar-expand">
					<div className="collapse navbar-collapse justify-content-between">
						<div className="header-left d-flex align-items-center">
							<MenuToggleButton />
							<div className="dashboard_bar tw-text-lg tw-font-semibold tw-text-gray-800 dark:tw-text-white">
								{isTakeExamPage ? (
									<span className="d-flex align-items-center gap-2" style={{ textTransform: "none" }}>
										<img src={avatarSrc} alt={displayName} className="tw-w-9 tw-h-9 tw-rounded-full tw-object-cover" />
										<span>{displayName}</span>
									</span>
								) : menuTitleKey ? (
									t(menuTitleKey)
								) : finalName.join(" ").length === 0 || finalName.join(" ") === "dashboard dark" ? (
									t("menu.dashboard.title")
								) : (
									finalName.join(" ")
								)}
							</div>
						</div>
						<ul className="navbar-nav header-right align-items-center">
							<LanguageSwitcher />

							<li className="nav-item dropdown notification_dropdown">
								<Link to={"#"} className={`nav-link bell dz-theme-mode ${background.value === "dark" ? "active" : ""}`}
									onClick={() => handleThemeMode()}
								>
									<i id="icon-light-1">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-sun"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>
									</i>
									<i id="icon-dark-1">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-moon"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
									</i>
								</Link>
							</li>

							<Dropdown as="li" className="nav-item notification_dropdown" onToggle={(isOpen) => { if (isOpen) loadRecentNotifications(); }}>
								<Dropdown.Toggle as="a" className="nav-link bell i-false c-pointer position-relative">
									<i className="ti ti-bell fs-20"></i>
									{unreadCount > 0 && (
										<span className="badge bg-danger rounded-circle position-absolute" style={{ width: 8, height: 8, top: 8, right: 8, padding: 0 }}></span>
									)}
								</Dropdown.Toggle>
								<Dropdown.Menu align="end" className="dropdown-menu-end p-0" style={{ minWidth: 320 }}>
									<div className="p-3 border-bottom d-flex align-items-center justify-content-between">
										<h6 className="mb-0">{t("menu.notifications")}</h6>
										{unreadCount > 0 && (
											<button type="button" className="btn btn-link btn-sm p-0" onClick={handleMarkAllRead}>
												{t("header.notifications.markAllRead")}
											</button>
										)}
									</div>
									{recentNotifications.length > 0 ? (
										recentNotifications.map((notif) => (
											<Dropdown.Item
												key={notif.id}
												as={Link}
												to="/notifications"
												className="d-flex align-items-center py-2"
												onClick={() => !notif.is_read && handleMarkRead(notif.id)}
											>
												<span className={`avatar avatar-sm ${notif.is_read ? "bg-secondary-transparent" : "bg-primary-transparent"} rounded-circle me-2 d-flex align-items-center justify-content-center flex-shrink-0`}>
													<i className={`ti ti-bell text-${notif.is_read ? "secondary" : "primary"} fs-14`}></i>
												</span>
												<div className="overflow-hidden">
													<p className="mb-0 text-truncate">{notif.title}</p>
													<small className="text-muted">{new Date(notif.created_at).toLocaleString(getLocale())}</small>
												</div>
											</Dropdown.Item>
										))
									) : (
										<div className="p-3 text-center text-muted small">{t("header.notifications.empty")}</div>
									)}
									<Dropdown.Item as={Link} to="/notifications" className="text-center border-top">
										{t("header.notifications.viewAll")}
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>


							<li className="nav-item tw-relative" ref={profileRef}>
								<button
									type="button"
									onClick={() => setProfileOpen((o) => !o)}
									className="nav-link i-false c-pointer ms-0 tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-0"
								>
									<div className="header-info2 d-flex align-items-center">
										<img src={avatarSrc} alt={displayName} className="tw-w-9 tw-h-9 tw-rounded-full tw-object-cover" />
									</div>
								</button>
								{profileOpen && (
									<div className="profile-dropdown-panel pt-6">
										<div className="card mb-0 shadow-none border-0">
											<div className="card-header p-3">
												<ul className="d-flex align-items-center">
													<li className="position-relative">
														<img
															src={avatarSrc}
															className="ms-0 tw-w-10 tw-h-10 tw-rounded-full tw-object-cover"
															alt={displayName}
															style={{ opacity: avatarUploading ? 0.5 : 1 }}
														/>
														<button
															type="button"
															title={t("header.avatar.changePhoto")}
															onClick={handleAvatarClick}
															disabled={avatarUploading}
															className="tw-absolute tw-bottom-0 tw-right-0 tw-w-4 tw-h-4 tw-rounded-full tw-bg-primary tw-border tw-border-white tw-flex tw-items-center tw-justify-center tw-p-0"
															style={{ fontSize: 8, lineHeight: 1 }}
														>
															<i className="ti ti-camera text-white"></i>
														</button>
														<input
															ref={avatarInputRef}
															type="file"
															accept="image/jpeg,image/png,image/webp"
															className="d-none"
															onChange={handleAvatarChange}
														/>
													</li>
													<li className="ms-2">
														<h4 className="mb-0 tw-text-gray-800 dark:tw-text-white">{displayName}</h4>
														<span className="tw-text-gray-400">{primaryRole ? t(`roles.${primaryRole}`, { defaultValue: primaryRole }) : ""}</span>
													</li>
												</ul>
												{avatarUploading && <div className="small text-muted mt-2">{t("header.avatar.uploading")}</div>}
												{avatarError && <div className="small text-danger mt-2">{avatarError}</div>}

											</div>
											<div className="card-footer text-center p-3">
												<LogoutPage />
											</div>
										</div>
									</div>
								)}
							</li>
						</ul>
					</div>
				</nav>
			</div>
		</div>
	);
};

export default Header;